import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, Search, Plus, X } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import RequireDealer from "@/components/RequireDealer";
import { useCart } from "@/context/CartContext";
import { PRICE_GROUPS, PRICE_STAND } from "@/data/priceList";
import { SHOP_IMAGES } from "@/data/shopImages";
import { loadBestand } from "@/utils/bestand";
import { formatEuro } from "@/utils/dealerSession";
import { useLang } from "@/i18n";

const CAP = 120;

function Shop() {
    const { t } = useLang();
    const cart = useCart();
    const [q, setQ] = useState("");
    const [group, setGroup] = useState("");
    // Bestandsliste live aus bestand.xlsx (Webroot) — Fallback: eingebaute Daten
    const [base, setBase] = useState(null);
    useEffect(() => {
        let alive = true;
        loadBestand().then((b) => { if (alive) setBase(b); });
        return () => { alive = false; };
    }, []);
    const groups = base ? base.groups : PRICE_GROUPS;
    const stand = base ? base.stand : PRICE_STAND;
    const imgMap = base ? base.images : SHOP_IMAGES;
    const [zoom, setZoom] = useState(null); // {sku, name, ref, src} für die Bild-Lupe
    useEffect(() => {
        if (!zoom) return;
        const onKey = (e) => { if (e.key === "Escape") setZoom(null); };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [zoom]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return groups.filter((g) => !group || g.title === group).flatMap((g) =>
            g.items
                .filter((it) => !needle || it.name.toLowerCase().includes(needle) || it.sku.includes(needle) || (it.ref || "").toLowerCase().includes(needle))
                .map((it) => ({ ...it, group: g.title }))
        );
    }, [q, group, groups]);

    const shown = filtered.slice(0, CAP);

    return (
        <main data-testid="page-shop" className="pt-28 sm:pt-32 pb-40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumb">
                    <Link to="/" data-testid="breadcrumb-home" className="hover:text-red-700 transition-colors">{t("Start", "Home")}</Link>
                    <span className="mx-2">/</span>
                    <span className="text-slate-600">{t("Händler-Shop", "Dealer shop")}</span>
                </p>

                <Reveal>
                    <div className="mt-6 max-w-3xl">
                        <Eyebrow>{t("Händler-Shop · Bestellsystem", "Dealer shop · Ordering system")}</Eyebrow>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                            {t("Fachhandelskatalog: direkt online bestellen.", "Trade catalogue: order directly online.")}
                        </h1>
                        <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base">
                            {t(
                                `Alle Artikel aus dem Fachhandelskatalog (Stand ${stand}). Artikel in den Warenkorb legen und die Bestellung direkt an unseren Vertrieb senden — wir bestätigen Verfügbarkeit und verbindliche Preise.`,
                                `All items from the trade catalogue (as of ${stand}). Add items to the cart and send the order straight to our sales team — we will confirm availability and binding prices.`
                            )}
                        </p>
                    </div>
                </Reveal>

                <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="shop-controls">
                    <label className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="search"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={t("Suchen: Artikel, Bezeichnung oder Typ …", "Search: item, description or type …")}
                            data-testid="shop-search-input"
                            className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:outline-none"
                        />
                    </label>
                    <select
                        value={group}
                        onChange={(e) => setGroup(e.target.value)}
                        data-testid="shop-group-select"
                        className="rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 focus:border-red-500 focus:outline-none"
                    >
                        <option value="">{t("Alle Produktgruppen", "All product groups")}</option>
                        {groups.map((g) => (
                            <option key={g.title} value={g.title}>{g.title}</option>
                        ))}
                    </select>
                </div>

                {!base && (
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm px-6 py-16 text-center" data-testid="shop-loading">
                        <p className="text-sm font-semibold text-slate-500">{t("Bestandsliste wird geladen …", "Loading stock list …")}</p>
                    </div>
                )}
                {base && base.quelle === "fallback" && (
                    <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="shop-bestand-fallback">
                        {t(
                            "Hinweis: bestand.xlsx wurde auf dem Server nicht gefunden oder kann nicht gelesen werden — es gilt der eingebaute Datenstand.",
                            "Note: bestand.xlsx was not found on the server or cannot be read — the built-in data applies."
                        )}
                    </p>
                )}
                {base && (
                <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white" data-testid="shop-table">
                        <table className="w-full text-sm min-w-[700px]">
                            <thead>
                                <tr className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-widest text-slate-500">
                                    <th className="px-4 py-3" />
                                    <th className="px-5 py-3 font-medium">Art.-Nr.</th>
                                    <th className="px-5 py-3 font-medium">{t("Bezeichnung", "Description")}</th>
                                    <th className="px-5 py-3 font-medium text-right">{t("Preis", "Price")}</th>
                                    <th className="px-5 py-3 font-medium text-center">{t("Menge", "Qty")}</th>
                                    <th className="px-5 py-3" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {shown.map((it) => (
                                    <tr key={it.sku} className="hover:bg-slate-50/70 transition-colors">
                                        <td className="px-4 py-3">
                                            {imgMap[it.sku] ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setZoom({ sku: it.sku, name: it.name, ref: it.ref, src: imgMap[it.sku] })}
                                                    title={t("Bild vergrößern", "Enlarge image")}
                                                    aria-label={t(`Bild ${it.sku} vergrößern`, `Enlarge image ${it.sku}`)}
                                                    data-testid={`shop-img-btn-${it.sku}`}
                                                    className="block cursor-zoom-in rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                                                >
                                                    <img
                                                        src={imgMap[it.sku]}
                                                        alt={it.name}
                                                        loading="lazy"
                                                        data-testid={`shop-img-${it.sku}`}
                                                        className="h-12 w-12 rounded-lg border border-slate-200 bg-white object-contain"
                                                    />
                                                </button>
                                            ) : (
                                                <span className="block h-12 w-12 rounded-lg border border-dashed border-slate-200" aria-hidden="true" />
                                            )}
                                        </td>
                                        <td className="px-5 py-3 font-mono text-red-700 whitespace-nowrap">{it.sku}</td>
                                        <td className="px-5 py-3">
                                            {it.ref && <span className="font-mono text-xs font-semibold text-red-700">Typ {it.ref}</span>}
                                            <span className={`block text-slate-900 ${it.ref ? "mt-0.5" : ""}`}>{it.name}</span>
                                            <span className="block text-xs text-slate-400 mt-0.5">{it.group}</span>
                                        </td>
                                        <td className="px-5 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">{formatEuro(it.price)}</td>
                                        <td className="px-5 py-3 text-center">
                                            <input
                                                id={`qty-shop-${it.sku}`}
                                                type="number"
                                                min="1"
                                                max="9999"
                                                defaultValue="1"
                                                data-testid={`shop-qty-${it.sku}`}
                                                className="w-16 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-center focus:border-red-500 focus:outline-none"
                                            />
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <button
                                                onClick={() => {
                                                    const el = document.getElementById(`qty-shop-${it.sku}`);
                                                    const qty = parseInt((el && el.value) || "1", 10) || 1;
                                                    cart.add(it, qty);
                                                }}
                                                data-testid={`shop-add-${it.sku}`}
                                                className="inline-flex items-center gap-1.5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-semibold px-4 py-2 transition-colors"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                {t("Hinzufügen", "Add")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {shown.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="px-5 py-10 text-center text-slate-500" data-testid="shop-no-results">
                                            {t("Keine Treffer — bitte Suchbegriff oder Produktgruppe anpassen.", "No matches — please adjust the search term or product group.")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    )}
                    {base && filtered.length > CAP && (
                        <p className="mt-3 text-xs text-slate-500" data-testid="shop-cap-note">
                            {t(
                                `${filtered.length} Artikel gefunden — die ersten ${CAP} werden angezeigt. Bitte suchen oder Produktgruppe wählen, um weiter einzuschränken.`,
                                `${filtered.length} items found — showing the first ${CAP}. Please search or pick a product group to narrow down.`
                            )}
                        </p>
                    )}
                    <p className="mt-4 text-xs text-slate-400" data-testid="shop-netto-note">
                        {t(
                            "Alle Preise verstehen sich netto in Euro, zuzüglich gesetzlicher Mehrwertsteuer und zzgl. Versandkosten, ab Lager Hemhofen. Irrtümer vorbehalten — verbindlich ist die Auftragsbestätigung des Vertriebs.",
                            "All prices are net in EUR, plus statutory VAT and shipping costs, ex warehouse Hemhofen. Subject to errors — the sales team's order confirmation is binding."
                        )}
                    </p>
            </div>

            <AnimatePresence>
                {zoom && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-4 sm:p-10 cursor-zoom-out"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        onClick={() => setZoom(null)}
                        data-testid="shop-zoom-overlay"
                    >
                        <motion.figure
                            className="relative max-h-full max-w-full"
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ duration: 0.18, ease: "easeOut" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={zoom.src}
                                alt={zoom.name}
                                className="max-h-[76vh] max-w-full w-auto rounded-xl border border-white/20 bg-white object-contain shadow-2xl"
                                data-testid="shop-zoom-image"
                            />
                            <figcaption className="mt-3 max-w-xl text-center text-sm text-white/85 mx-auto">
                                <span className="font-mono text-red-300">Art.-Nr. {zoom.sku}</span>
                                {zoom.ref ? ` · Typ ${zoom.ref}` : ""} — {zoom.name}
                            </figcaption>
                            <button
                                type="button"
                                onClick={() => setZoom(null)}
                                aria-label="Schließen"
                                data-testid="shop-zoom-close"
                                className="absolute -top-3 -right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg hover:bg-slate-100 transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>

            {cart.count > 0 && (
                <div className="fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-3" data-testid="shop-cart-bar">
                    <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                        <span className="inline-flex items-center gap-2.5 text-sm font-semibold text-slate-900" data-testid="cart-bar-summary">
                            <span className="relative inline-flex items-center justify-center h-9 w-9 rounded-full bg-red-50 border border-red-100 text-red-700">
                                <ShoppingCart className="h-4 w-4" />
                            </span>
                            {t(`${cart.count} Artikel im Warenkorb`, `${cart.count} items in cart`)}
                            <span className="hidden sm:inline text-slate-400">·</span>
                            <span className="hidden sm:inline">{formatEuro(cart.totalCents)}</span>
                        </span>
                        <Link
                            to="/haendler/warenkorb"
                            data-testid="cart-bar-link"
                            className="inline-flex items-center gap-2 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-6 py-2.5 transition-colors"
                        >
                            {t("Zum Warenkorb", "Go to cart")}
                            <ShoppingCart className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            )}
        </main>
    );
}

export default function ShopPage() {
    return (
        <RequireDealer>
            <Shop />
        </RequireDealer>
    );
}
